/**
 * paymentStatus.ts
 * Square決済DB (DS1: search_by_contact.php) のデータから
 * バッジ・チップ表示用のステータスと金額を計算するユーティリティ。
 *
 * 【金額の単位について（重要・2026-08-05 修正）】
 * Square の Money.amount は「その通貨の最小単位」。JPY は zero-decimal currency なので
 * **最小単位 = 1円**、つまり API が返す 154200 はそのまま ¥154,200 である。
 * 以前は payments / refunds の金額を /100 していたため 1/100 の額が出ていた
 * （invoice.computed_amount は元から /100 せず正しく表示できていたので気付かれなかった）。
 *
 * 【返金ステータス（2026-08-05 追加）】
 * Square の invoice.status には PAID / PARTIALLY_PAID / UNPAID / DRAFT / CANCELED の他に
 * REFUNDED（全額返金）/ PARTIALLY_REFUNDED（一部返金）がある。
 * 返金後は PAID ではなくなるため、これを明示的に扱わないと「未決済」に誤判定される。
 */

export interface SquarePaymentEntry {
  invoices: any[];
  payments: any[];
  orders: any[];
  refunds: any[];
  fetchedAt: number;
}

export interface PaymentStatus {
  /** バッジテキスト: 'SQ済' | '済' | '部分' | '返金' | '未払' | '' */
  text: string;
  /** バッジ色 (hex) */
  color: string;
  /** 決済総額（返金を差し引く前の実際に支払われた額・円） */
  totalPaidAmount: number;
  /** 返金があるか */
  hasRefunds: boolean;
  /** 返金合計 (円) */
  refundAmount: number;
  /** 実質支払額 = totalPaidAmount - refundAmount (円) */
  netAmount: number;
  /** 全額返金済みか（実質支払額が 0 以下） */
  isFullyRefunded: boolean;
}

/** 「支払いが完了して入金がある」とみなす invoice ステータス */
const SETTLED_STATUSES = ['PAID', 'PARTIALLY_REFUNDED', 'REFUNDED'];

/** 未入金とみなす invoice ステータス */
const UNPAID_STATUSES = ['UNPAID', 'DRAFT', 'SCHEDULED'];

/** 集計対象外（取消・失敗） */
const VOID_STATUSES = ['CANCELED', 'FAILED'];

export interface InvoiceStatusChip {
  label: string;
  bg: string;
  color: string;
}

/**
 * Square invoice.status → 表示チップ（ラベル・配色）
 * SquarePaymentInfo / MobileSquareInfo / DayList 支払確認モーダルで共用する。
 *
 * 未知のステータスは「未決済」ではなく status 文字列そのものを出す。
 * （未知の値を無条件に「未決済」に丸めたのが今回の PARTIALLY_REFUNDED 誤表示の原因）
 */
export function invoiceStatusChip(status: string): InvoiceStatusChip {
  switch (status) {
    case 'PAID':
      return { label: '決済済', bg: '#e8f5e9', color: '#2e7d32' };
    case 'PARTIALLY_REFUNDED':
      return { label: '一部返金', bg: '#fff7ed', color: '#c2410c' };
    case 'REFUNDED':
      return { label: '返金済', bg: '#ffebee', color: '#c62828' };
    case 'PARTIALLY_PAID':
      return { label: '一部決済', bg: '#e0f2fe', color: '#0369a1' };
    case 'CANCELED':
      return { label: '取消済', bg: '#f1f5f9', color: '#64748b' };
    case 'FAILED':
      return { label: '失敗', bg: '#fee2e2', color: '#b91c1c' };
    case 'DRAFT':
      return { label: '下書き', bg: '#e0e7ff', color: '#3730a3' };
    case 'SCHEDULED':
      return { label: '送信予約', bg: '#e0e7ff', color: '#3730a3' };
    case 'UNPAID':
      return { label: '未決済', bg: '#fff3e0', color: '#e65100' };
    default:
      return { label: status || '不明', bg: '#fff3e0', color: '#e65100' };
  }
}

export interface SquareAmounts {
  /** 決済総額（返金前・円） */
  paidAmount: number;
  /** 返金総額（円） */
  refundAmount: number;
  /** 実質支払額 = paidAmount - refundAmount（円・負にはしない） */
  netAmount: number;
  hasRefunds: boolean;
}

/** 返金として集計しないステータス */
const DEAD_REFUND_STATUSES = ['REJECTED', 'FAILED'];

/**
 * エントリから 決済額 / 返金額 / 実質支払額 を算出する。
 *
 * 決済額のソース優先順位:
 *   1. payments[].total_amount (status=COMPLETED)
 *   2. invoices[].total_completed_amount — payments が同期されていない場合のフォールバック
 * 返金額のソース優先順位:
 *   1. refunds[].amount (REJECTED/FAILED を除く)
 *   2. payments[].refunded_amount — sq_refunds に行が無い場合のフォールバック
 */
export function computeSquareAmounts(
  entry: SquarePaymentEntry | undefined | null,
): SquareAmounts {
  const zero: SquareAmounts = {
    paidAmount: 0,
    refundAmount: 0,
    netAmount: 0,
    hasRefunds: false,
  };
  if (!entry) return zero;

  const payments = Array.isArray(entry.payments) ? entry.payments : [];
  const refunds = Array.isArray(entry.refunds) ? entry.refunds : [];
  const invoices = Array.isArray(entry.invoices) ? entry.invoices : [];

  // --- 決済額 ---
  let paidAmount = payments
    .filter((p: any) => p.status === 'COMPLETED')
    .reduce((sum: number, p: any) => sum + (Number(p.total_amount) || 0), 0);

  if (paidAmount === 0) {
    // payments 未同期時のフォールバック: invoice の入金済み合計を使う
    paidAmount = invoices
      .filter((inv: any) => !VOID_STATUSES.includes(inv.status))
      .reduce(
        (sum: number, inv: any) => sum + (Number(inv.total_completed_amount) || 0),
        0,
      );
  }

  // --- 返金額 ---
  let refundAmount = refunds
    .filter((r: any) => !DEAD_REFUND_STATUSES.includes(r.status))
    .reduce((sum: number, r: any) => sum + (Number(r.amount) || 0), 0);

  if (refundAmount === 0) {
    // sq_refunds に行が無い場合のフォールバック（payment.refunded_amount）
    refundAmount = payments.reduce(
      (sum: number, p: any) => sum + (Number(p.refunded_amount) || 0),
      0,
    );
  }

  return {
    paidAmount,
    refundAmount,
    netAmount: Math.max(0, paidAmount - refundAmount),
    hasRefunds: refundAmount > 0,
  };
}

/**
 * Square 側で「入金が完了している」か（手動フラグを考慮しない純粋な Square 判定）。
 *
 * - PAID / PARTIALLY_REFUNDED → 入金済み（一部返金でも「決済はされた」）
 * - REFUNDED（全額返金） → false。返金で手元に残っていないため支払い済み扱いにしない
 * - UNPAID / DRAFT / PARTIALLY_PAID が1件でもあれば false
 *
 * 支払い済みフラグの自動ON・手動ON時の注意表示に使用（2026-07-26 / 2026-08-05 返金対応）。
 */
export function isSquarePaid(entry: SquarePaymentEntry | undefined | null): boolean {
  if (!entry || !Array.isArray(entry.invoices) || entry.invoices.length === 0) return false;

  // 取消済み・失敗の請求書は判定から除外（削除→再発行の運用で残ることがある）
  const live = entry.invoices.filter((inv: any) => !VOID_STATUSES.includes(inv.status));
  if (live.length === 0) return false;

  const allSettled = live.every((inv: any) => SETTLED_STATUSES.includes(inv.status));
  if (!allSettled) return false;

  // 全額返金されている場合は「支払い済み」とみなさない
  const { paidAmount, refundAmount } = computeSquareAmounts(entry);
  if (paidAmount > 0 && refundAmount >= paidAmount) return false;
  if (live.every((inv: any) => inv.status === 'REFUNDED')) return false;

  return true;
}

/**
 * DS1のキャッシュエントリからバッジ表示用のステータスを計算する
 *
 * @param entry     DS1キャッシュエントリ (undefined = データなし)
 * @param isManualPaid  detail.payment === 1 (手動確認済みフラグ)
 * @param isLoading データ取得中かどうか
 */
export function computePaymentStatus(
  entry: SquarePaymentEntry | undefined,
  isManualPaid: boolean,
  isLoading: boolean,
): PaymentStatus {
  const empty: PaymentStatus = {
    text: '',
    color: '#757575',
    totalPaidAmount: 0,
    hasRefunds: false,
    refundAmount: 0,
    netAmount: 0,
    isFullyRefunded: false,
  };

  // Loading
  if (isLoading) {
    return { ...empty, text: '...' };
  }

  // 確認済みフラグ かつ キャッシュなし → 「済」
  if (isManualPaid && !entry) {
    return { ...empty, text: '済', color: '#4caf50' };
  }

  // データなし
  if (!entry) {
    return empty;
  }

  const invoices = Array.isArray(entry.invoices) ? entry.invoices : [];

  // Invoice がない場合
  if (invoices.length === 0) {
    if (isManualPaid) {
      return { ...empty, text: '済', color: '#4caf50' };
    }
    return empty;
  }

  const { paidAmount, refundAmount, netAmount, hasRefunds } = computeSquareAmounts(entry);

  // 取消済み・失敗を除いた「生きている」請求書で判定する
  const live = invoices.filter((inv: any) => !VOID_STATUSES.includes(inv.status));
  const hasSettled = live.some((inv: any) => SETTLED_STATUSES.includes(inv.status));
  const hasUnpaid = live.some((inv: any) => UNPAID_STATUSES.includes(inv.status));
  const hasPartialPaid = live.some((inv: any) => inv.status === 'PARTIALLY_PAID');
  const allSettled = live.length > 0 && live.every((inv: any) => SETTLED_STATUSES.includes(inv.status));

  const isFullyRefunded =
    (live.length > 0 && live.every((inv: any) => inv.status === 'REFUNDED')) ||
    (paidAmount > 0 && refundAmount >= paidAmount);

  const base = {
    totalPaidAmount: paidAmount,
    hasRefunds,
    refundAmount,
    netAmount,
    isFullyRefunded,
  };

  // --- バッジ判定 (優先順位順) ---
  if (isFullyRefunded && hasSettled) {
    // 全額返金: 決済はされたが手元に残っていない
    return { ...base, text: '返金', color: '#f44336' };
  }
  if (allSettled) {
    // PAID / PARTIALLY_REFUNDED のみ → 決済済み（一部返金は返金有バッジで併記）
    return { ...base, text: 'SQ済', color: '#4caf50' };
  }
  if (isManualPaid) {
    return { ...base, text: '済', color: '#4caf50' };
  }
  if (hasPartialPaid) {
    return { ...base, text: '部分', color: '#2196f3' };
  }
  if (hasUnpaid) {
    return { ...base, text: '未払', color: '#ff9800' };
  }

  return { ...base, text: '', color: '#757575' };
}
